// src/pages/MFAVerifyCode/MFAVerifyCode.tsx
import { FC, useState, useEffect, useContext, useCallback } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { mfaService } from 'features/mfa';
import './MFAVerifyCode.scss';
import { delete_files } from "features/ai-assistant";
import { UserContext } from "features/account";
import CodeInput from "features/mfa/components/CodeInput/CodeInput";
import Button, { Button_Style, Button_Type } from "shared/ui/Button/Button";
import { MFASetupModal } from "features/mfa";

const MFAVerifyCode: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setAi_conversation } = useContext(UserContext);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaType, setMfaType] = useState<'mobile'>('mobile');
  const [mfaEmail, setMfaEmail] = useState<string>('');
  const [showMFASetupModal, setShowMFASetupModal] = useState(false);
  const type = user?.type ?? 'individual'

  useEffect(() => {
    const state = location.state as any;
    if (state?.mfaToken && state?.mfaType) {
      setMfaToken(state.mfaToken);
      setMfaType(state.mfaType);
      setMfaEmail(state.mfaEmail || '');
    } else {
      navigate(-1);
    }
  }, [location, navigate]);

  const handleVerify = useCallback(async () => {
    if (!code || code.length !== 6) {
      setError('Veuillez entrer un code à 6 chiffres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const request = await mfaService.verifyMFALogin(mfaToken, code, mfaType, type);

      if (request?.type === "individual") {
        await delete_files(user?.clientNumber!);
        localStorage.removeItem("assistantConversation");
        setAi_conversation([]);
        if (request?.mustRedirectToIDAssistant) {
          navigate("/particuliers/id-assistant/add/standard");
        } else {
          navigate("/particuliers/dashboard");
        }
      } else if (request?.type === "partner") {
        if (!!+(process.env.REACT_APP_FOR_PARTNER_ONLY || 0)) {
          navigate("/professionnels/base-de-donnees");
        } else {
          navigate("/professionnels/dashboard");
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Code invalide');
    } finally {
      setLoading(false);
    }
  }, [code, mfaToken, mfaType, user, navigate, setAi_conversation]);

  const handleTryAnotherMethod = () => {
    setShowMFASetupModal(true);
  };

  const handleMFASetupClose = () => {
    setShowMFASetupModal(false);
  };

  const handleMFASetupSuccess = () => {
    setShowMFASetupModal(false);
    // Après activation d'une autre méthode, on peut rediriger ou rester
  };

  return (
    <div>
      <div className="mfa-verify-code-page">
        <div className="container">
          <div className="card">
            <div className="header">
              <h2>Code d'authentification</h2>
              <p className="subtitle">
                Ouvrez votre application d'authentification<br />
                et saisissez le code à 6 chiffres
              </p>
            </div>

            <CodeInput
              value={code}
              onChange={setCode}
              length={6}
              error={error}
              disabled={loading}
              autoFocus={true}
              onComplete={handleVerify}
            />

            <div className="actions">
              <Button
                type={Button_Type.PRIMARY}
                style={Button_Style.SOLID}
                label={loading ? "Vérification..." : "Vérifier"}
                onClick={handleVerify}
                disabled={loading || code.length !== 6}
                fullWidth
              />
            </div>

            <div className="alternative-method">
              <Button
                type={Button_Type.SECONDARY}
                style={Button_Style.OUTLINED}
                label="Autre méthode"
                onClick={handleTryAnotherMethod}
                fullWidth
              />
            </div>

            <div className="help">
              <p>Vous n'avez pas accès à votre application ?</p>
              <ul>
                <li>Utilisez un code de secours</li>
                <li onClick={() => {
                  window.open("https://idprotect.fr/contact/", "_blank", "noopener,noreferrer");
                }}>Contactez le support client</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* Modal pour choisir une autre méthode MFA */}
      {showMFASetupModal && (
        <MFASetupModal
          isOpen={showMFASetupModal}
          onClose={handleMFASetupClose}
          onSuccess={handleMFASetupSuccess}
          userEmail={mfaEmail}
          fromLogin={true}
          mfaToken={mfaToken}
        />
      )}
    </div>
  );
};

export default MFAVerifyCode;